import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import styles from './Register.module.css'
import ErrorIcon from '../components/icons/ErrorIcon'
import SuccessIcon from '../components/icons/SuccessIcon'
import TuidaIcon from '../components/icons/TuidaIcon'

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [formErrors, setFormErrors] = useState({})
  const [touched, setTouched] = useState({})
  
  const { register, error, clearError, loading, isAuthenticated } = useAuth()
  const navigate = useNavigate()

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/')
    }
  }, [isAuthenticated, navigate])

  // Clear errors when component unmounts
  useEffect(() => {
    return () => clearError()
  }, [clearError])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
    
    // Clear global error
    if (error) {
      clearError()
    }
  }

  const handleBlur = (e) => {
    const { name } = e.target
    setTouched(prev => ({
      ...prev,
      [name]: true
    }))
    
    // Validate individual field on blur
    validateField(name, formData[name])
  }

  const validateField = (fieldName, value) => {
    const errors = { ...formErrors }

    switch (fieldName) {
      case 'username':
        if (!value.trim()) {
          errors.username = 'Username is required'
        } else if (!/^[a-zA-Z0-9_]{3,20}$/.test(value)) {
          errors.username = 'Username must be 3-20 characters and contain only letters, numbers, and underscores'
        } else {
          delete errors.username
        }
        break

      case 'email':
        if (!value.trim()) {
          errors.email = 'Email is required'
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          errors.email = 'Email address is invalid'
        } else {
          delete errors.email
        }
        break

      case 'password':
        if (!value) {
          errors.password = 'Password is required'
        } else if (value.length < 4) {
          errors.password = 'Password must be at least 4 characters'
        } else {
          delete errors.password
        }
        
        // Also validate confirm password if it's been touched
        if (touched.confirmPassword) {
          if (formData.confirmPassword !== value) {
            errors.confirmPassword = 'Passwords do not match'
          } else {
            delete errors.confirmPassword
          }
        }
        break

      case 'confirmPassword':
        if (!value) {
          errors.confirmPassword = 'Please confirm your password'
        } else if (value !== formData.password) {
          errors.confirmPassword = 'Passwords do not match'
        } else {
          delete errors.confirmPassword
        }
        break

      default:
        break
    }

    setFormErrors(errors)
  }

  const validateForm = () => {
    const errors = {}

    // Username validation
    if (!formData.username.trim()) {
      errors.username = 'Username is required'
    } else if (!/^[a-zA-Z0-9_]{3,20}$/.test(formData.username)) {
      errors.username = 'Username must be 3-20 characters and contain only letters, numbers, and underscores'
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email address is invalid'
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required'
    } else if (formData.password.length < 4) {
      errors.password = 'Password must be at least 4 characters'
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password'
    } else if (formData.confirmPassword !== formData.password) {
      errors.confirmPassword = 'Passwords do not match'
    }

    setFormErrors(errors)
    setTouched({
      username: true,
      email: true,
      password: true,
      confirmPassword: true
    })

    return Object.keys(errors).length === 0
  }

  const getInputClass = (fieldName) => {
    let inputClass = styles.formInput
    
    if (touched[fieldName]) {
      if (formErrors[fieldName]) {
        inputClass += ` ${styles.formInputError}`
      } else if (formData[fieldName]) {
        inputClass += ` ${styles.formInputSuccess}`
      }
    }
    
    return inputClass
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    const { confirmPassword, ...registerData } = formData
    const result = await register(registerData)
    
    if (result.success) {
      navigate('/')
    }
  }

  const isFormValid = () => {
    return formData.username && 
           formData.email && 
           formData.password && 
           formData.confirmPassword && 
           Object.keys(formErrors).length === 0
  }

 
  return (
    <div className={styles.registerContainer}>
      <div className={styles.registerWrapper}>
        <div className={styles.header}>
          <div className={styles.logo}>
            <TuidaIcon />
          </div>
          <h2 className={styles.title}>
            Create your account
          </h2>
          <p className={styles.subtitle}>
            Join the internal resource management platform of Plane Care Ltd.
          </p>
          <p className={styles.loginPrompt}>
            Already have an account?{' '}
            <Link
              to="/login"
              className={styles.link}
            >
              Sign in here
            </Link>
          </p>
        </div>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.card}>
            {error && (
              <div className={styles.errorAlert}>
                <ErrorIcon />
                <p className={styles.errorText}>{error}</p>
              </div>
            )}

            <div className={styles.formGroup}>
              <label htmlFor="username" className={`${styles.formLabel} ${styles.required}`}>
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                value={formData.username}
                onChange={handleChange}
                onBlur={handleBlur}
                className={getInputClass('username')}
                placeholder="Enter your username"
                required
              />
              {touched.username && formErrors.username && (
                <p className={`${styles.validationMessage} ${styles.errorMessage}`}>
                  <ErrorIcon />
                  {formErrors.username}
                </p>
              )}
              {touched.username && !formErrors.username && formData.username && (
                <p className={`${styles.validationMessage} ${styles.successMessage}`}>
                  <SuccessIcon />
                  Username looks good!
                </p>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="email" className={`${styles.formLabel} ${styles.required}`}>
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className={getInputClass('email')}
                placeholder="Enter your email address"
                required
              />
              {touched.email && formErrors.email && (
                <p className={`${styles.validationMessage} ${styles.errorMessage}`}>
                  <ErrorIcon />
                  {formErrors.email}
                </p>
              )}
              {touched.email && !formErrors.email && formData.email && (
                <p className={`${styles.validationMessage} ${styles.successMessage}`}>
                  <SuccessIcon />
                  Email looks good!
                </p>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="password" className={`${styles.formLabel} ${styles.required}`}>
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                className={getInputClass('password')}
                placeholder="Create a password (min. 6 characters)"
                required
              />
              {touched.password && formErrors.password && (
                <p className={`${styles.validationMessage} ${styles.errorMessage}`}>
                  <ErrorIcon />
                  {formErrors.password}
                </p>
              )}
              {touched.password && !formErrors.password && formData.password && (
                <p className={`${styles.validationMessage} ${styles.successMessage}`}>
                  <SuccessIcon />
                  Password is secure!
                </p>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="confirmPassword" className={`${styles.formLabel} ${styles.required}`}>
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                className={getInputClass('confirmPassword')}
                placeholder="Re-enter your password"
                required
              />
              {touched.confirmPassword && formErrors.confirmPassword && (
                <p className={`${styles.validationMessage} ${styles.errorMessage}`}>
                  <ErrorIcon />
                  {formErrors.confirmPassword}
                </p>
              )}
              {touched.confirmPassword && !formErrors.confirmPassword && formData.confirmPassword && (
                <p className={`${styles.validationMessage} ${styles.successMessage}`}>
                  <SuccessIcon />
                  Passwords match!
                </p>
              )}
            </div>

            <div className={styles.submitSection}>
              <button
                type="submit"
                disabled={loading || !isFormValid()}
                className={styles.submitButton}
              >
                {loading ? (
                  <>
                    <div className={styles.loadingSpinner}></div>
                    Creating Account...
                  </>
                ) : (
                  'Create Account'
                )}
              </button>
            </div>

            <div className={styles.footer}>
              <p className={styles.footerText}>
                By creating an account, you agree to our{' '}
                <a href="#" className={styles.footerLink}>
                  {/* Terms of Service */}
                  Internal Rules
                </a>{' '}
                and{' '}
                <a href="#" className={styles.footerLink}>
                  Workmanship Policy
                </a>
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Register